import React, { useMemo, useState } from "react";
import { api } from "../../util/api";
import { useHistory } from "react-router-dom";
import BidDetailModal from "../../page/search/BidDetailModal";

import {
    Box,
    IconButton,
    CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AppAutocomplete from "../../component/common/AppAutocomplete";
import ComboBox from "../../component/common/ComboBox";
import { notify } from "../../util/toast";

const SEARCH_TYPES = [
    { label: "기업 검색", value: "기업 검색" },
    { label: "과업 검색 (공고명)", value: "과업 검색 (공고명)" },
    { label: "과업 검색 (입찰공고번호)", value: "과업 검색 (입찰공고번호)" },
];

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/**
 * UnifiedSearchBar — 나라장터 통합 검색바.
 *
 * <p>좌측 검색 타입 select + 우측 자동완성 input + 검색 아이콘 버튼.
 * 이전엔 직접 fetch + 자체 Collapse/Paper/List 로 suggestion drop 을 구현했으나,
 * 다른 화면들과 일관성을 맞추기 위해 공용 {@link AppAutocomplete} 으로 통합 — debounce/로딩/Popper 표준화.
 *
 * <h3>동작 보존</h3>
 * <ul>
 *   <li>2자 이상 입력 시 호출 / 옵션 선택된 상태에서는 fetcher 호출 안 함 (기존 정책)</li>
 *   <li>Enter → handleSearch — 선택된 옵션이 없으면 경고 노출</li>
 *   <li>옵션 선택 → keyword 에 label set + selectedItem 보관 → 다음 검색 트리거 대기</li>
 *   <li>좌측 검색 타입 변경 시 keyword/selectedItem 모두 리셋</li>
 * </ul>
 *
 * props:
 *  - variant: "row" | "compact" (기본 row)
 *  - fullWidth: boolean (기본 true)
 */
export default function UnifiedSearchBar({ variant = "row", fullWidth = true }) {
    const history = useHistory();

    const [keyword, setKeyword] = useState("");
    const [searchType, setSearchType] = useState("기업 검색");
    const [selectedItem, setSelectedItem] = useState(null);
    const [loading, setLoading] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedBidNumber, setSelectedBidNumber] = useState(null);
    const [selectedBidOrder, setSelectedBidOrder] = useState("000");

    const placeholder = useMemo(() => {
        if (searchType === "기업 검색") return "기업명을 입력하세요";
        if (searchType === "과업 검색 (공고명)") return "공고명을 입력하세요";
        return "입찰공고번호를 입력하세요";
    }, [searchType]);

    const fetcher = async (q) => {
        // 2자 미만이거나 이미 선택된 항목이 있으면 호출 안 함 — 기존 정책 보존.
        if (q.trim().length < 2 || selectedItem) return [];
        let url;
        if (searchType === "기업 검색") {
            url = `${API_BASE}/companies/autocomplete?keyword=${encodeURIComponent(q)}`;
        } else if (searchType === "과업 검색 (공고명)") {
            url = `${API_BASE}/bids/autocomplete/title?keyword=${encodeURIComponent(q)}`;
        } else {
            url = `${API_BASE}/bids/autocomplete/notice-number?keyword=${encodeURIComponent(q)}`;
        }
        try {
            const data = await api.get(url);
            return Array.isArray(data) ? data : (data?.data || []);
        } catch {
            return [];
        }
    };

    const handleSearch = async () => {
        const trimmed = keyword.trim();
        if (!trimmed) return;

        setLoading(true);
        try {
            if (!selectedItem) {
                notify("목록에서 선택된 값이 아닙니다.", "warning");
                return;
            }
            if (searchType === "기업 검색") {
                history.push(`/search/companies/?bizNumber=${encodeURIComponent(selectedItem.bizNumber)}`);
            } else {
                const bidKey = selectedItem.bidNoticeNumber || selectedItem.title;
                setSelectedBidNumber(bidKey);
                setSelectedBidOrder(selectedItem.bidNoticeOrder || "000");
                setModalOpen(true);
            }
        } finally {
            setLoading(false);
            setSelectedItem(null);
            setKeyword("");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSearch();
    };

    const rootSx = { display: "flex", gap: 1, alignItems: "center", width: fullWidth ? "100%" : "auto" };

    return (
        <Box sx={{ position: "relative", width: fullWidth ? "100%" : "auto" }}>
            <Box sx={rootSx}>
                <Box sx={{ width: 220, flexShrink: 0 }}>
                    <ComboBox
                        clearable={false}
                        placeholder="검색 타입"
                        options={SEARCH_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                        value={searchType}
                        onChange={(v) => {
                            setSearchType(v);
                            setKeyword("");
                            setSelectedItem(null);
                        }}
                    />
                </Box>

                <AppAutocomplete
                    freeSolo
                    value={selectedItem}
                    inputValue={keyword}
                    onChange={(v) => {
                        if (v && typeof v !== "string") {
                            const label = v.name || v.title || v.bidNoticeNumber || "";
                            setSelectedItem(v);
                            setKeyword(label);
                        }
                    }}
                    onInputChange={(v) => {
                        setKeyword(v);
                        if (selectedItem) setSelectedItem(null);
                    }}
                    onKeyDown={handleKeyDown}
                    fetcher={fetcher}
                    fetcherDeps={[searchType, selectedItem]}
                    fetchOnOpen={false}
                    getOptionLabel={(o) => typeof o === "string" ? o : (o.name || o.title || o.bidNoticeNumber || "")}
                    // 같은 공고번호의 차수 다른 항목이 동일 key 로 충돌하지 않도록 (공고번호-차수) 복합 key.
                    getOptionKey={(o) => {
                        if (typeof o === "string") return o;
                        if (o.bidNoticeNumber) return `${o.bidNoticeNumber}-${o.bidNoticeOrder ?? ""}`;
                        return o.bizNumber || o.id || o.name || o.title || "";
                    }}
                    // 기업: 사업자번호 / 공고: 공고번호-차수 (차수까지 보여 동일 제목·번호도 구분 가능).
                    secondaryText={(o) => {
                        if (typeof o === "string") return "";
                        if (o.bizNumber) return o.bizNumber;
                        if (o.bidNoticeNumber) {
                            return o.bidNoticeOrder
                                ? `${o.bidNoticeNumber} · ${o.bidNoticeOrder}차`
                                : o.bidNoticeNumber;
                        }
                        return "";
                    }}
                    placeholder={placeholder}
                    variant="outlined" disableUnderline={false}
                    sx={{ flex: 1 }}
                />

                <IconButton
                    onClick={handleSearch}
                    disabled={loading || !keyword.trim()}
                    aria-label="search"
                    sx={{ flexShrink: 0 }}
                >
                    {loading ? <CircularProgress size={18} /> : <SearchIcon />}
                </IconButton>
            </Box>

            {modalOpen && selectedBidNumber && (
                <BidDetailModal
                    isOpen={modalOpen}
                    bidNumber={selectedBidNumber}
                    bidNoticeOrder={selectedBidOrder}
                    onClose={() => setModalOpen(false)}
                />
            )}
        </Box>
    );
}
